import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { BUSINESS_TOOL_MODE } from 'constants/businessTool';
import {
  ACCOUNT_PAYMENT_INFO,
  ACCOUNT_SETTINGS,
  ACCOUNT_SUBSCRIPTIONS,
  CONTACT_US_MAIL,
  HELP_AND_SUPPORT,
  HOME,
  SUBSCRIPTION_PLAN,
} from 'constants/paths';
import { useBusinessToolSessionStore } from 'store/useBusinessToolStore';
import { getSetBusinessToolMode } from 'store/useBusinessToolStore/selectors';

import { ReactComponent as ArrowRightEndIcon } from 'assets/icons/ArrowRightEndIcon.svg';

import usePromiseStatus from 'hooks/usePromiseStatus';

import { IAccountSettingItem } from 'interfaces/account';

import { userLogOut } from 'helpers/authentication.utils';
import ToastService from 'helpers/ToastService';

export const useAccountSettingsData = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { track, isPending } = usePromiseStatus();
  const setBusinessToolMode = useBusinessToolSessionStore(getSetBusinessToolMode);

  const logOutHandler = async (): Promise<void> => {
    try {
      await track(userLogOut());
      setBusinessToolMode(BUSINESS_TOOL_MODE.EDIT)
      localStorage.removeItem('business-tool');
    } catch (error) {
      if (error instanceof Error) {
        ToastService.showError('Logout Failed');
      }
    } finally {
      navigate(HOME);
    }
  };

  const menuContent: IAccountSettingItem[] = useMemo(
    () => [
      { title: t('accountSettings'), clickHandler: () => navigate(ACCOUNT_SETTINGS) },
      { title: t('subscriptions'), clickHandler: () => navigate(ACCOUNT_SUBSCRIPTIONS) },
      { title: t('paymentInfo'), clickHandler: () => navigate(ACCOUNT_PAYMENT_INFO) },
      { title: t('viewPlansButton'), clickHandler: () => navigate(SUBSCRIPTION_PLAN) },
      {
        title: t('helpAndSupport'),
        clickHandler: () => window.open(HELP_AND_SUPPORT, '_blank', 'noopener,noreferrer'),
      },
      { title: t('contactUs'), clickHandler: () => (window.location.href = `mailto:${CONTACT_US_MAIL}`) },
      {
        title: t('logOutButton'),
        clickHandler: logOutHandler,
        icon: <ArrowRightEndIcon />,
      },
    ],
    [t, navigate, logOutHandler],
  );

  return {
    menuContent,
    isLogOutPending: isPending,
  };
};
